/**
 * Sync Drive trip UI state (active multi-stop plan + current trips) to the API
 * so PWA and website share the same trips.
 *
 * sessionStorage remains the fast local cache; server is source of truth across devices.
 */

import api from "../api/client";
import {
  loadDriveMultiStopPlan,
  hydrateDriveMultiStopPlan,
} from "./driveMultiStopPlan";
import {
  loadCurrentDriveTrips,
  hydrateCurrentDriveTrips,
  isInProcessDriveTrip,
} from "./driveCurrentTrips";
import {
  archiveDriveTripToHistory,
  loadDriveTripHistory,
  mergeRecentTripsIntoHistory,
} from "./driveTripHistory";

const PUSH_DEBOUNCE_MS = 450;
let pushTimer = null;
let pushInFlight = null;
let hydrating = false;
let lastHydratedUserId = null;
let lastPushedAt = 0;
let listenerBound = false;

function notifyHistoryUi() {
  try {
    window.dispatchEvent(new Event("smartRoute:tripHistoryChanged"));
  } catch {
    /* ignore */
  }
}

function tripOrderFingerprint(plan) {
  const ids = (Array.isArray(plan?.stops) ? plan.stops : [])
    .flatMap((s) => [
      ...(Array.isArray(s?.previousOrderIds) ? s.previousOrderIds : []),
      s?.orderId,
    ])
    .map((id) => String(id || "").trim())
    .filter(Boolean)
    .sort();
  return ids.join(",");
}

function stopStatusRank(status) {
  const st = String(status || "").toLowerCase();
  if (st === "taken") return 3;
  if (st === "ready") return 2;
  if (st === "ordered") return 1;
  if (st === "open") return 0;
  return -1;
}

function stopOrderIds(stop) {
  return [
    ...(Array.isArray(stop?.previousOrderIds) ? stop.previousOrderIds : []),
    stop?.orderId,
  ]
    .map((id) => String(id || "").trim())
    .filter(Boolean);
}

/** Prefer ready/taken over ordered when merging the same trip from two sources. */
function mergePlanPreferAdvanced(incoming, existing) {
  if (!incoming) return existing || null;
  if (!existing?.stops?.length) return incoming;
  if (!incoming?.stops?.length) return existing;

  const existingByOrder = new Map();
  for (const s of existing.stops) {
    for (const id of stopOrderIds(s)) existingByOrder.set(id, s);
  }

  const stops = incoming.stops.map((s) => {
    let best = s;
    for (const id of stopOrderIds(s)) {
      const other = existingByOrder.get(id);
      if (!other) continue;
      if (stopStatusRank(other.status) > stopStatusRank(best.status)) {
        best = {
          ...best,
          status: other.status,
          orderStatus: other.orderStatus || other.status,
          readyAt: other.readyAt || best.readyAt || null,
          takenAt: other.takenAt || best.takenAt || null,
          takenOrderIds: other.takenOrderIds || best.takenOrderIds,
        };
      } else if (
        String(other.status) === "ready" &&
        String(best.status) === "ready" &&
        !best.readyAt &&
        other.readyAt
      ) {
        best = { ...best, readyAt: other.readyAt };
      }
    }
    return best;
  });
  const tripStartedAt = existing.tripStartedAt || incoming.tripStartedAt || null;
  const merged = {
    ...incoming,
    stops,
    tripStartedAt,
  };
  // An empty navigationOpenedAt only means "notified, Maps not open yet" after start.
  if (!tripStartedAt) {
    delete merged.navigationOpenedAt;
  } else if (Object.prototype.hasOwnProperty.call(existing, "navigationOpenedAt")) {
    merged.navigationOpenedAt = existing.navigationOpenedAt || incoming.navigationOpenedAt || null;
  } else if (Object.prototype.hasOwnProperty.call(incoming, "navigationOpenedAt")) {
    merged.navigationOpenedAt = incoming.navigationOpenedAt || null;
  }
  return merged;
}

function localClientUpdatedAt() {
  const plan = loadDriveMultiStopPlan();
  const trips = loadCurrentDriveTrips();
  let max = Number(plan?.savedAt) || 0;
  for (const t of trips) {
    const parked = t?.parkedAt ? new Date(t.parkedAt).getTime() : 0;
    const saved = Number(t?.savedAt) || 0;
    max = Math.max(max, parked || 0, saved || 0);
  }
  return max || Date.now();
}

function snapshotLocalState() {
  return {
    activePlan: loadDriveMultiStopPlan(),
    currentTrips: loadCurrentDriveTrips(),
    recentTrips: loadDriveTripHistory(),
    clientUpdatedAt: localClientUpdatedAt(),
  };
}

function hasMeaningfulLocalState(state) {
  const plan = state?.activePlan;
  const trips = Array.isArray(state?.currentTrips) ? state.currentTrips : [];
  const recent = Array.isArray(state?.recentTrips) ? state.recentTrips : [];
  const paidLocal =
    Array.isArray(plan?.stops) &&
    plan.stops.some((s) => {
      const st = String(s?.status || "").toLowerCase();
      return st === "ordered" || st === "ready" || st === "taken";
    });
  const paidParked = trips.some(
    (t) =>
      !t?.inProcess &&
      Array.isArray(t?.stops) &&
      t.stops.some((s) => {
        const st = String(s?.status || "").toLowerCase();
        return st === "ordered" || st === "ready" || st === "taken";
      }),
  );
  return Boolean(paidLocal || paidParked || (plan?.stops?.length && plan.origin) || recent.length);
}

function hasMeaningfulServerState(data) {
  return hasMeaningfulLocalState({
    activePlan: data?.activePlan,
    currentTrips: data?.currentTrips,
    recentTrips: data?.recentTrips,
  });
}

/**
 * Debounced push of local Drive state to the server (logged-in users only).
 */
export function scheduleDriveTripStatePush() {
  if (hydrating) return;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    pushDriveTripState().catch(() => {});
  }, PUSH_DEBOUNCE_MS);
}

let lastVisibilityHydrateAt = 0;

function ensureLocalChangeListener() {
  if (listenerBound || typeof window === "undefined") return;
  listenerBound = true;
  window.addEventListener("smartRoute:driveTripLocalChanged", () => {
    scheduleDriveTripStatePush();
  });
  window.addEventListener("smartRoute:driveTripFlush", () => {
    if (pushTimer) {
      clearTimeout(pushTimer);
      pushTimer = null;
    }
    pushDriveTripState().catch(() => {});
  });
  // Pull again when returning to the tab (PWA ↔ website / another device).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (!lastHydratedUserId) return;
    const now = Date.now();
    if (now - lastVisibilityHydrateAt < 5000) return;
    lastVisibilityHydrateAt = now;
    hydrateDriveTripsFromServer(lastHydratedUserId, { force: true }).catch(() => {});
  });
}

ensureLocalChangeListener();

export async function pushDriveTripState() {
  if (hydrating) return null;
  if (pushInFlight) return pushInFlight;

  pushInFlight = (async () => {
    try {
      const body = snapshotLocalState();
      lastPushedAt = body.clientUpdatedAt;
      const res = await api.put("/api/drive-trips", body, {
        skipSessionExpired: true,
      });
      if (res?.data?.skipped && res?.data?.data) {
        // Server had a newer snapshot — adopt it.
        applyServerState(res.data.data, { notify: true, localBefore: body });
      }
      // Pull any server-only recent trips (e.g. owner closed while we pushed).
      if (Array.isArray(res?.data?.data?.recentTrips)) {
        if (mergeRecentTripsIntoHistory(res.data.data.recentTrips)) notifyHistoryUi();
      }
      return res?.data || null;
    } catch (err) {
      const status = err?.response?.status;
      // Guest / logged out — ignore.
      if (status === 401 || status === 403) return null;
      return null;
    } finally {
      pushInFlight = null;
    }
  })();

  return pushInFlight;
}

function applyServerState(data, { notify = true, localBefore = null } = {}) {
  // Server may have closed trips (owner confirm) — move them into Recent before wiping Current.
  const remoteRecent = Array.isArray(data?.recentTrips) ? data.recentTrips : [];
  const addedFromServer = mergeRecentTripsIntoHistory(remoteRecent);

  const remoteCurrent = Array.isArray(data?.currentTrips) ? data.currentTrips : [];
  const remoteFps = new Set(
    remoteCurrent.map((t) => tripOrderFingerprint(t)).filter(Boolean),
  );
  const localCurrent = Array.isArray(localBefore?.currentTrips)
    ? localBefore.currentTrips
    : loadCurrentDriveTrips();
  let archivedLocal = 0;
  const keepLocalUnfinished = [];
  const localInProcess = localCurrent.filter((trip) => isInProcessDriveTrip(trip));
  for (const trip of localCurrent) {
    if (isInProcessDriveTrip(trip)) continue;
    const fp = tripOrderFingerprint(trip);
    if (fp && remoteFps.has(fp)) continue;
    // Still awaiting pickup — never treat as closed just because the server snapshot lags.
    const unfinished = (Array.isArray(trip?.stops) ? trip.stops : []).some((s) => {
      const st = String(s?.status || "");
      return st === "ordered" || st === "ready";
    });
    if (unfinished) {
      keepLocalUnfinished.push(trip);
      continue;
    }
    if (archiveDriveTripToHistory(trip, { closedAt: new Date().toISOString() })) {
      archivedLocal += 1;
    }
  }

  const localActive = localBefore?.activePlan ?? loadDriveMultiStopPlan();
  let nextActive = data?.activePlan || null;
  if (localActive?.stops?.length && !data?.activePlan) {
    const hasUnfinished = (Array.isArray(localActive.stops) ? localActive.stops : []).some((s) => {
      const st = String(s?.status || "");
      return st === "ordered" || st === "ready";
    });
    if (hasUnfinished) {
      nextActive = localActive;
    } else {
      const hasPaid = (Array.isArray(localActive.stops) ? localActive.stops : []).some((s) => {
        const st = String(s?.status || "");
        return st === "ordered" || st === "ready" || st === "taken";
      });
      if (hasPaid) {
        if (archiveDriveTripToHistory(localActive, { closedAt: new Date().toISOString() })) {
          archivedLocal += 1;
        }
      }
    }
  } else if (localActive?.stops?.length && nextActive?.stops?.length) {
    nextActive = mergePlanPreferAdvanced(nextActive, localActive);
  }

  const localByFp = new Map();
  for (const trip of localCurrent) {
    if (isInProcessDriveTrip(trip)) continue;
    const fp = tripOrderFingerprint(trip);
    if (fp) localByFp.set(fp, trip);
  }
  const mergedRemote = remoteCurrent
    .filter((trip) => {
      if (!isInProcessDriveTrip(trip)) return true;
      return !localInProcess.some((local) => {
        if (String(local.id || "") && String(local.id) === String(trip.id || "")) return true;
        const sameFrom =
          String(local.origin?.formattedAddress || local.origin?.address || "")
            .trim()
            .toLowerCase() ===
          String(trip.origin?.formattedAddress || trip.origin?.address || "")
            .trim()
            .toLowerCase();
        const sameTo =
          String(local.destination?.formattedAddress || local.destination?.address || "")
            .trim()
            .toLowerCase() ===
          String(trip.destination?.formattedAddress || trip.destination?.address || "")
            .trim()
            .toLowerCase();
        return sameFrom && sameTo;
      });
    })
    .map((trip) => {
      const fp = tripOrderFingerprint(trip);
      const local = fp ? localByFp.get(fp) : null;
      return local ? mergePlanPreferAdvanced(trip, local) : trip;
    });

  const mergedCurrent = [...localInProcess, ...keepLocalUnfinished, ...mergedRemote];
  hydrateDriveMultiStopPlan(nextActive, { notify });
  hydrateCurrentDriveTrips(mergedCurrent, { notifyUi: notify });
  if (notify && (addedFromServer || archivedLocal || keepLocalUnfinished.length)) {
    notifyHistoryUi();
  }
  // Push kept unfinished trips after hydrate finishes (hydrating blocks immediate push).
  if (keepLocalUnfinished.length || (nextActive && !data?.activePlan)) {
    setTimeout(() => {
      try {
        scheduleDriveTripStatePush();
      } catch {
        /* ignore */
      }
    }, 0);
  }
}

/**
 * Pull Drive trip state after login / app boot and merge with local session.
 * Prefer the newer clientUpdatedAt; if only one side has paid trips, prefer that.
 */
export async function hydrateDriveTripsFromServer(userId, { force = false } = {}) {
  const uid = String(userId || "").trim();
  if (!uid) return { ok: false };

  ensureLocalChangeListener();

  // Avoid hammering on every render when AuthContext re-sets the same user.
  if (
    !force &&
    lastHydratedUserId === uid &&
    Date.now() - lastPushedAt < 2000
  ) {
    return { ok: true, skipped: true };
  }

  hydrating = true;
  try {
    const local = snapshotLocalState();
    const res = await api.get("/api/drive-trips", { skipSessionExpired: true });
    if (!res?.data?.ok) {
      hydrating = false;
      if (hasMeaningfulLocalState(local)) {
        await pushDriveTripState();
      }
      return { ok: false };
    }

    const remote = res.data.data || {};
    const remoteAt = Number(remote.clientUpdatedAt) || 0;
    const localAt = Number(local.clientUpdatedAt) || 0;
    const localMeaningful = hasMeaningfulLocalState(local);
    const remoteMeaningful = hasMeaningfulServerState(remote);

    if (!remoteMeaningful && localMeaningful) {
      hydrating = false;
      await pushDriveTripState();
      lastHydratedUserId = uid;
      return { ok: true, source: "local" };
    }

    if (remoteMeaningful && !localMeaningful) {
      applyServerState(remote, { notify: true, localBefore: local });
      lastHydratedUserId = uid;
      lastPushedAt = remoteAt || Date.now();
      return { ok: true, source: "server" };
    }

    if (remoteMeaningful && localMeaningful) {
      if (remoteAt >= localAt) {
        applyServerState(remote, { notify: true, localBefore: local });
        lastHydratedUserId = uid;
        lastPushedAt = remoteAt;
        return { ok: true, source: "server" };
      }
      hydrating = false;
      await pushDriveTripState();
      lastHydratedUserId = uid;
      return { ok: true, source: "local" };
    }

    lastHydratedUserId = uid;
    return { ok: true, source: "none" };
  } catch {
    return { ok: false };
  } finally {
    hydrating = false;
  }
}

export function resetDriveTripSyncState() {
  lastHydratedUserId = null;
  lastPushedAt = 0;
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
}
